import { useTranslation } from '@thoughtspot/i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { VariableAssignmentOperation, VariableType, } from "../../../services/generated/graphql-types";
import { useGetOrgsListQuery } from "../../../services/org/org-service";
import { useCreateVariableMutation, useSearchVariablesLazyQuery, useUpdateVariableMutation, } from "../../../services/variables/variables-service";
import { extractErrorCode } from '../utils';
import { MODE, TranslationsKeys, VARIABLE_NAME_EXISTS_ERROR_CODE, } from '../variable-interface';
import { trackVariableCreate, trackVariableEdit } from '../variables-analytics';
import { CreateEditForm } from './create-edit-form.container';
import { useVariableNameCheck } from './hooks/useVariableNameCheck';
export const CreateEditModal = ({ isModalOpened, closeModal, isEditMode, variableData, setIsLoading, successBannerMode, onSuccess, setErrorBanner, setErrorBannerMessage, }) => {
    const { t } = useTranslation();
    const orgListInput = useMemo(() => ({
        sort: 'DEFAULT',
        sortascending: true,
        batchsize: -1,
        offset: 0,
        pattern: '',
    }), []);
    const [nameError, setNameError] = useState(null);
    const hasHandledErrorRef = useRef(false);
    const { checkVariableNameExists, isCheckingVariableName, nameError: hookNameError, clearNameError, } = useVariableNameCheck(useSearchVariablesLazyQuery);
    const combinedNameError = nameError || hookNameError;
    const { data: orgsList, loading: orgsLoading, refetch: refreshOrgList, } = useGetOrgsListQuery(orgListInput);
    useEffect(() => {
        if (isModalOpened) {
            refreshOrgList({ data: orgListInput });
        }
    }, [isModalOpened, orgListInput, refreshOrgList]);
    const orgList = useMemo(() => orgsList?.Data?.orgs ?? [], [
        orgsList?.Data?.orgs,
    ]);
    const shouldShowLoading = Boolean(isEditMode && orgsLoading);
    useEffect(() => {
        if (!setIsLoading) {
            return;
        }
        setIsLoading(shouldShowLoading);
    }, [setIsLoading, shouldShowLoading]);
    const { data: createVariableData, error: createVariableError, loading: createVariableLoading, createVariable, } = useCreateVariableMutation();
    const { data: updateVariableData, error: updateVariableError, loading: updateVariableLoading, updateVariable, } = useUpdateVariableMutation();
    useEffect(() => {
        if (createVariableData) {
            successBannerMode(MODE.ADD);
            onSuccess?.();
            closeModal();
            setNameError(null);
            clearNameError();
            setErrorBanner(false);
            setErrorBannerMessage(null);
        }
    }, [
        createVariableData,
        closeModal,
        successBannerMode,
        onSuccess,
        clearNameError,
        setErrorBanner,
        setErrorBannerMessage,
    ]);
    useEffect(() => {
        if (!createVariableLoading &&
            createVariableError &&
            !hasHandledErrorRef.current) {
            const errorCode = extractErrorCode(createVariableError);
            trackVariableCreate(false, { errorCode });
            const isDuplicateNameError = errorCode === VARIABLE_NAME_EXISTS_ERROR_CODE;
            if (isDuplicateNameError && !isEditMode) {
                const errorMsg = t(TranslationsKeys.ERROR_NAME_EXISTS);
                setNameError(errorMsg);
                hasHandledErrorRef.current = true;
            }
            else {
                closeModal();
                setErrorBanner(true);
                setErrorBannerMessage(t(TranslationsKeys.ERROR_CREATE));
            }
        }
    }, [
        createVariableError,
        createVariableLoading,
        t,
        isEditMode,
        closeModal,
        setErrorBanner,
        setErrorBannerMessage,
    ]);
    useEffect(() => {
        if (updateVariableData) {
            successBannerMode(MODE.EDIT);
            closeModal();
            setErrorBanner(false);
            setErrorBannerMessage(null);
            setNameError(null);
            clearNameError();
            if (onSuccess) {
                onSuccess();
            }
        }
    }, [
        updateVariableData,
        closeModal,
        successBannerMode,
        onSuccess,
        clearNameError,
        setErrorBanner,
        setErrorBannerMessage,
    ]);
    useEffect(() => {
        if (!updateVariableLoading && updateVariableError) {
            trackVariableEdit(false);
            closeModal();
            setErrorBanner(true);
            setErrorBannerMessage(t(TranslationsKeys.ERROR_UPDATE));
        }
    }, [
        updateVariableError,
        updateVariableLoading,
        t,
        closeModal,
        setErrorBanner,
        setErrorBannerMessage,
    ]);
    const handleFormSuccess = useCallback(async (formData) => {
        setErrorBanner(false);
        setErrorBannerMessage(null);
        setNameError(null);
        clearNameError();
        hasHandledErrorRef.current = false;
        const processVariableValues = (value) => {
            if (formData.variableType === VariableType.FormulaVariable &&
                value) {
                return value
                    .split(';')
                    .map((v) => v.trim())
                    .filter((v) => v.length > 0);
            }
            return [value];
        };
        if (isEditMode) {
            const originalValues = new Map();
            variableData?.values?.forEach(val => {
                if (!val.org_identifier)
                    return;
                originalValues.set(val.org_identifier, Array.isArray(val.value_list)
                    ? val.value_list.join(';')
                    : val.value || '');
            });
            const currentOrgIds = new Set(formData.orgMappings
                .map(m => m.orgIdentifier)
                .filter(Boolean));
            const upserts = formData.orgMappings
                .filter(m => {
                const orig = originalValues.get(m.orgIdentifier);
                return orig !== (m.value || '');
            })
                .map(m => ({
                assigned_values: processVariableValues(m.value),
                org_identifier: m.orgIdentifier,
                operation: originalValues.has(m.orgIdentifier)
                    ? VariableAssignmentOperation.Replace
                    : VariableAssignmentOperation.Add,
            }));
            const removes = (formData.removedMappings || [])
                .filter(m => !currentOrgIds.has(m.orgIdentifier))
                .map(m => ({
                assigned_values: [],
                org_identifier: m.orgIdentifier,
                operation: VariableAssignmentOperation.Remove,
            }));
            const changes = [...upserts, ...removes];
            await updateVariable({
                identifier: formData.name,
                variable_assignment: changes,
            });
            trackVariableEdit(true, {
                variable_type: variableData?.variable_type || '',
                add_count: upserts.filter(a => a.operation === VariableAssignmentOperation.Add).length,
                replace_count: upserts.filter(a => a.operation === VariableAssignmentOperation.Replace).length,
                remove_count: removes.length,
            });
        }
        else {
            const assignmentsWithScopes = formData.orgMappings.map(mapping => ({
                variable_assignment: {
                    operation: VariableAssignmentOperation.Add,
                    variable_values: processVariableValues(mapping.value),
                },
                variable_value_scope: {
                    org_identifier: mapping.orgIdentifier,
                },
            }));
            const variableInput = {
                name: formData.name,
                type: formData.variableType,
                is_sensitive: formData.sensitive,
                variable_assignments_with_scopes: assignmentsWithScopes.length > 0
                    ? assignmentsWithScopes
                    : undefined,
                ...(formData.variableType ===
                    VariableType.FormulaVariable && {
                    data_type: 'VARCHAR',
                }),
            };
            await createVariable(variableInput);
            trackVariableCreate(true, {
                variable_type: formData.variableType,
                sensitive: formData.sensitive,
                data_type: formData.variableType === VariableType.FormulaVariable
                    ? 'VARCHAR'
                    : undefined,
                org_count: formData.orgMappings.length,
            });
        }
    }, [
        isEditMode,
        variableData,
        setErrorBanner,
        setErrorBannerMessage,
        clearNameError,
        updateVariable,
        createVariable,
    ]);
    const clearExternalNameError = useCallback(() => {
        setNameError(null);
        clearNameError();
        hasHandledErrorRef.current = true;
    }, [clearNameError]);
    return (React.createElement(React.Fragment, null,
        React.createElement(CreateEditForm, { isModalOpened: isModalOpened, closeModal: closeModal, isEditMode: isEditMode, variableData: variableData, orgList: orgList, onSuccess: handleFormSuccess, externalNameError: combinedNameError, clearExternalNameError: clearExternalNameError, isOrgsLoading: orgsLoading, checkVariableNameExists: checkVariableNameExists, isCheckingVariableName: isCheckingVariableName })));
};
//# sourceMappingURL=create-edit-modal.container.js.map